import type { ItineraryWithDetails } from "@mountayaapp/types/api/api";

import type { RouteGeometry } from "./cameraPlan";
import { haversineKm } from "./elevation";
import { extractLineStrings } from "./geometry";
import { segmentsForSelection } from "./selectors";

/**
 * A LngLat is a [longitude, latitude] coordinate, the 2D pair the camera
 * pipeline samples a route in. It matches RouteGeometry.line's element shape.
 */
export type LngLat = [number, number];

const METERS_PER_KM = 1_000;
const TO_RAD = Math.PI / 180;
const TO_DEG = 180 / Math.PI;

/**
 * RouteCursor is a sampled position along a route: the interpolated [lng, lat]
 * `point`, the `bearingDeg` of the route's tangent there (the heading the path
 * is travelling, 0..360), and the `index` of the line vertex at or before the
 * point (so a caller can read a per-vertex channel such as elevation without a
 * second search). Produced by cursorAtFraction.
 */
export interface RouteCursor {
  bearingDeg: number;
  index: number;
  point: LngLat;
}

/**
 * cumulativeMeters returns the running arc-length in metres at each vertex of a
 * line: index 0 is 0, and each later entry adds the great-circle distance from
 * the previous vertex. The result has one entry per input vertex, so the total
 * route length is its last element. A line of fewer than two vertices yields a
 * single 0 (or an empty array for an empty line). This is the one length pass
 * the fraction lookups share, so the route is measured identically everywhere.
 */
export const cumulativeMeters = (line: readonly LngLat[]): number[] => {
  const out = new Array<number>(line.length);
  let running = 0;
  for (let index = 0; index < line.length; index += 1) {
    if (index > 0) {
      const previous = line[index - 1];
      const current = line[index];
      if (previous && current) {
        running += haversineKm(previous, current) * METERS_PER_KM;
      }
    }

    out[index] = running;
  }

  return out;
};

/**
 * routeLengthMeters is the total great-circle length of a line in metres — the
 * last cumulative-distance entry, or 0 for a line that cannot describe a length.
 */
export const routeLengthMeters = (line: readonly LngLat[]): number => {
  const cumulative = cumulativeMeters(line);

  return cumulative[cumulative.length - 1] ?? 0;
};

/**
 * findVertexIndex returns the index i such that
 * cumulative[i] <= targetMeters < cumulative[i + 1], the vertex at or before the
 * target. Returns 0 below the first vertex and the last index at or above the
 * end. O(log n) binary search over the ascending cumulative array, so a dense
 * route costs a logarithmic lookup per sampled frame rather than a linear scan.
 */
export const findVertexIndex = (cumulative: readonly number[], targetMeters: number): number => {
  const length = cumulative.length;
  if (length === 0) {
    return 0;
  }

  const first = cumulative[0];
  const last = cumulative[length - 1];
  // Compare against `=== undefined`, not falsy: a legitimate cumulative entry is
  // 0 (the first vertex), and `!0` would pin every lookup to index 0.
  if (first === undefined || last === undefined) {
    return 0;
  }

  if (targetMeters <= first) {
    return 0;
  }

  if (targetMeters >= last) {
    return length - 1;
  }

  let lo = 0;
  let hi = length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    const value = cumulative[mid];
    if (value === undefined) {
      break;
    }

    if (value <= targetMeters) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return lo;
};

/**
 * pointAtMeters returns the [lng, lat] located at the given arc-length along the
 * line, linearly interpolating between the two vertices whose cumulative
 * distances bracket the target. The target is clamped to the line, so a value
 * below 0 resolves to the first vertex and one past the end to the last. An
 * empty line yields [0, 0]. The optional precomputed `cumulative` avoids
 * re-measuring the line when the caller already holds it (the per-frame path).
 */
export const pointAtMeters = (
  line: readonly LngLat[],
  targetMeters: number,
  cumulative: readonly number[] = cumulativeMeters(line),
): LngLat => {
  const first = line[0];
  if (!first) {
    return [0, 0];
  }

  const last = line[line.length - 1] ?? first;
  const total = cumulative[cumulative.length - 1] ?? 0;
  if (targetMeters <= 0 || total === 0) {
    return [first[0], first[1]];
  }

  if (targetMeters >= total) {
    return [last[0], last[1]];
  }

  const lo = findVertexIndex(cumulative, targetMeters);
  const a = line[lo];
  const b = line[lo + 1];
  const aMeters = cumulative[lo];
  const bMeters = cumulative[lo + 1];
  if (!a || !b || aMeters === undefined || bMeters === undefined) {
    return [first[0], first[1]];
  }

  const span = bMeters - aMeters;
  const ratio = span === 0 ? 0 : (targetMeters - aMeters) / span;

  return [a[0] + (b[0] - a[0]) * ratio, a[1] + (b[1] - a[1]) * ratio];
};

/**
 * bearingDegBetween returns the initial great-circle bearing in degrees (0..360)
 * from a to b — the heading to travel to go from the first point to the second.
 * Two identical points have no defined heading and return 0.
 */
export const bearingDegBetween = (a: LngLat, b: LngLat): number => {
  if (a[0] === b[0] && a[1] === b[1]) {
    return 0;
  }

  const lat1 = a[1] * TO_RAD;
  const lat2 = b[1] * TO_RAD;
  const dLng = (b[0] - a[0]) * TO_RAD;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  return (Math.atan2(y, x) * TO_DEG + 360) % 360;
};

/**
 * tangentBearingAtMeters returns the route's heading in degrees (0..360) at the
 * given arc-length, sampled by looking `lookaheadMeters` further along the line
 * and taking the bearing from the cursor to that ahead-point. A larger lookahead
 * yields a steadier heading through dense vertices (the camera anticipates turns
 * rather than twitching); the ahead-point is clamped to the route end. Falls
 * back to the heading of the bracketing leg when the lookahead collapses to the
 * same point (at the very end of the route). The optional precomputed
 * `cumulative` is reused on the per-frame path.
 */
export const tangentBearingAtMeters = (
  line: readonly LngLat[],
  targetMeters: number,
  lookaheadMeters: number,
  cumulative: readonly number[] = cumulativeMeters(line),
): number => {
  const total = cumulative[cumulative.length - 1] ?? 0;
  const here = pointAtMeters(line, targetMeters, cumulative);
  const aheadMeters = Math.min(targetMeters + Math.max(0, lookaheadMeters), total);
  const ahead = pointAtMeters(line, aheadMeters, cumulative);
  if (here[0] !== ahead[0] || here[1] !== ahead[1]) {
    return bearingDegBetween(here, ahead);
  }

  // At the route end the lookahead has nowhere to go: fall back to the heading
  // of the final leg so the camera keeps its last travelling orientation rather
  // than snapping to 0.
  const lastIndex = findVertexIndex(cumulative, targetMeters);
  const previous = line[Math.max(0, lastIndex - 1)];
  const current = line[lastIndex];
  if (previous && current) {
    return bearingDegBetween(previous, current);
  }

  return 0;
};

/**
 * cursorAtFraction samples a route at a progress fraction (0..1 of total
 * length): it returns the interpolated point, the tangent bearing there (using
 * `lookaheadMeters` for a steady heading), and the index of the vertex at or
 * before the point. The fraction is clamped to [0, 1] so an out-of-range value
 * resolves to an endpoint. This is the single seam the resolver and the
 * generators sample the route through, so progress maps to a position the same
 * way for every consumer.
 */
export const cursorAtFraction = (
  line: readonly LngLat[],
  fraction: number,
  lookaheadMeters: number,
  cumulative: readonly number[] = cumulativeMeters(line),
): RouteCursor => {
  const total = cumulative[cumulative.length - 1] ?? 0;
  const clamped = Math.min(1, Math.max(0, fraction));
  const targetMeters = clamped * total;

  return {
    bearingDeg: tangentBearingAtMeters(line, targetMeters, lookaheadMeters, cumulative),
    index: findVertexIndex(cumulative, targetMeters),
    point: pointAtMeters(line, targetMeters, cumulative),
  };
};

/**
 * itineraryToRouteGeometry flattens an itinerary (optionally narrowed to one
 * segment) into the single RouteGeometry a Camera Plan resolves against: the
 * ordered segment LineStrings concatenated head-to-tail into one `line`, plus the
 * matching `elevations` when every vertex carries a third (altitude) coordinate.
 * It reuses the shared geometry extraction — segmentsForSelection for the ordered
 * segments and extractLineStrings for each segment's route line out of its
 * GeometryCollection — so it reads the itinerary the same way map fitting does,
 * never re-parsing geojson by hand. `segmentId` null spans the whole itinerary;
 * a non-null id narrows the flight to that one segment.
 *
 * Elevations are kept only when the count matches the line exactly (every vertex
 * has an altitude); a route mixing 2D and 3D vertices drops the elevations rather
 * than handing the resolver a mismatched array (elevationAtFraction requires the
 * counts to match). Returns null when no usable line is present — no itinerary, an
 * unknown segment, or segments with no LineString geometry — so the caller (the
 * preview/record tools) surfaces a structured failure rather than resolving an
 * empty flight. A route shorter than two vertices is also rejected, since a flight
 * needs a direction (routeGeometrySchema's line minimum).
 */
export const itineraryToRouteGeometry = (
  itinerary: ItineraryWithDetails | null,
  segmentId: string | null = null,
): RouteGeometry | null => {
  const segments = segmentsForSelection(itinerary, segmentId);
  const line: LngLat[] = [];
  const elevations: number[] = [];
  let everyVertexHasElevation = true;

  for (const segment of segments) {
    for (const lineString of extractLineStrings(segment.geojson)) {
      for (const coordinate of lineString.coordinates) {
        const lng = coordinate[0];
        const lat = coordinate[1];
        if (lng === undefined || lat === undefined) {
          continue;
        }

        line.push([lng, lat]);
        const elevation = coordinate[2];
        if (elevation === undefined) {
          everyVertexHasElevation = false;
        } else {
          elevations.push(elevation);
        }
      }
    }
  }

  if (line.length < 2) {
    return null;
  }

  return {
    line,
    // Keep elevations only when one landed for every vertex; a partial set would
    // not align with the line and the zoom model would reject it anyway.
    ...(everyVertexHasElevation && elevations.length === line.length ? { elevations } : {}),
  };
};
